import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  signal,
  inject,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TimelineStayGateway } from '@wilson/api/gateway';

import { Store } from '@ngxs/store';
import {
  Shift,
  Stay,
  StayStatus,
  TimelineShift,
  TimelineStayManagementDrawerFormFieldEnum,
  TimelineStayManagementDrawerFormValue,
} from '@wilson/interfaces';
import {
  CreateStaysDrawerViewComponent,
  StayPDFType,
  StaysAttachmentsHelperService,
  StaysDrawerHelperService,
  StaysDrawerStateService,
} from '@wilson/non-domain-specific/stays-form-and-drawer';
import { SectorState } from '@wilson/sectors';
import {
  TimelineStaysSuggestionService,
  TimelineUpdateShiftV2,
  TimelineUpsertNewStay,
} from '@wilson/shift-timeline/state';
import { ShiftsService } from '@wilson/shifts';
import { NzMessageService } from 'ng-zorro-antd/message';
import { firstValueFrom, Subscription } from 'rxjs';

@Component({
  selector: 'wilson-timeline-create-stays-drawer',
  templateUrl: './timeline-create-stays-drawer.component.html',
  styleUrls: ['./timeline-create-stays-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, CreateStaysDrawerViewComponent],
  providers: [StaysAttachmentsHelperService],
})
export class TimelineCreateStaysDrawerComponent implements OnInit, OnDestroy {
  protected timelineStayGateway = inject(TimelineStayGateway);
  protected timelineStaysSuggestionService = inject(
    TimelineStaysSuggestionService,
  );
  protected readonly staysDrawerHelperService = inject(
    StaysDrawerHelperService,
  );
  protected readonly staysAttachmentsHelperService = inject(
    StaysAttachmentsHelperService,
  );
  private readonly staysDrawerStateService = inject(StaysDrawerStateService);
  private readonly translate = inject(TranslateService);
  private readonly nzMessageService = inject(NzMessageService);
  private readonly shiftsService = inject(ShiftsService);
  private store = inject(Store);

  protected isSaveOperationOnGoingSignal = signal(false);
  protected isDrawerVisible$ =
    this.staysDrawerStateService.isCreateStaysDrawerVisible$;

  previousShift$ = this.staysDrawerStateService.previousShift$;
  nextTimelineCard$ = this.staysDrawerStateService.nextTimelineCard$;
  staySuggestionInfo$ = this.staysDrawerStateService.staySuggestionInfo$;
  protected sector$ = this.store.select(SectorState.firstSector);
  private subscriptions = new Subscription();

  ngOnInit(): void {
    this.staysAttachmentsHelperService.stayAttachments = [];
    this.subscriptions.add(
      this.isDrawerVisible$.subscribe((isDrawerVisible) => {
        if (!isDrawerVisible) {
          this.staysDrawerStateService.resetSetShifts();
        }
      }),
    );
  }

  closeDrawer() {
    this.staysDrawerStateService.toggleCreateStaysDrawer(false);
    this.staysDrawerStateService.resetSetShifts();
  }

  protected async confirm(
    formValue: Partial<TimelineStayManagementDrawerFormValue>,
  ): Promise<Stay | null> {
    let stay!: Stay | null;
    const modalRef = this.staysAttachmentsHelperService.openLoaderDialog();
    this.isSaveOperationOnGoingSignal.set(true);
    const ignoreStayNecessaryFormValue =
      !!formValue[
        TimelineStayManagementDrawerFormFieldEnum.IsOverNightStayRequired
      ];

    const previousShift = await firstValueFrom(
      this.staysDrawerStateService.previousShift$,
    );

    if (ignoreStayNecessaryFormValue && previousShift) {
      await this.updateShiftNecessaryStay(
        previousShift,
        ignoreStayNecessaryFormValue,
      );
      stay = null;
    } else {
      if (previousShift) {
        stay = await this.createStay(
          previousShift,
          ignoreStayNecessaryFormValue,
          formValue,
        );
      }
    }
    modalRef.close();
    return stay;
  }

  protected async confirmAndCreateReimbursement(
    formValue: Partial<TimelineStayManagementDrawerFormValue>,
  ): Promise<void> {
    const stay = await this.confirm(formValue);
    if (stay) {
      try {
        const updatedStay = await firstValueFrom(
          this.timelineStayGateway.updateStay({
            id: stay.id,
            status: StayStatus.Requested,
          }),
        );
        await firstValueFrom(
          this.staysDrawerHelperService.createHotelReimbursement(
            stay,
            StayPDFType.Reimbursement,
          ),
        );
        this.store.dispatch(new TimelineUpsertNewStay(updatedStay));
        await this.timelineStaysSuggestionService.refreshStaysDataForSpecifiedUsers(
          [stay.userId],
        );
      } catch {
        this.nzMessageService.error(
          this.translate.instant('general.something_went_wrong'),
        );
      }
    }
  }

  private async updateShiftNecessaryStay(
    previousShift: TimelineShift,
    ignoreStayNecessaryFormValue: boolean,
  ) {
    try {
      await firstValueFrom(
        this.shiftsService.updateShift({
          id: previousShift.id,
          ignoreStayNecessary: ignoreStayNecessaryFormValue,
        } as Shift),
      );

      this.store.dispatch(
        new TimelineUpdateShiftV2({
          ...previousShift,
          ignoreStayNecessary: ignoreStayNecessaryFormValue,
        }),
      );

      this.nzMessageService.success(
        this.translate.instant('general.saved_successfully'),
      );

      this.isSaveOperationOnGoingSignal.set(false);
      this.staysDrawerStateService.toggleCreateStaysDrawer(false);
    } catch (error) {
      console.error('An error occurred while creating a stay:', error);
      this.nzMessageService.error(
        this.translate.instant('general.something_went_wrong'),
      );
      this.isSaveOperationOnGoingSignal.set(false);
      throw error;
    }
  }

  private async createStay(
    previousShift: TimelineShift,
    ignoreStayNecessaryFormValue: boolean,
    formValue: Partial<TimelineStayManagementDrawerFormValue>,
  ): Promise<Stay> {
    const staysObject =
      this.staysDrawerHelperService.prepareStaysObject(formValue);
    try {
      const result = await firstValueFrom(
        this.timelineStayGateway.createStay(staysObject),
      );
      if (result) {
        await this.staysAttachmentsHelperService.handleFileUpload(result.id);

        await firstValueFrom(
          this.shiftsService.updateShift({
            id: previousShift.id,
            ignoreStayNecessary: ignoreStayNecessaryFormValue,
          } as Shift),
        );

        const stays = await firstValueFrom(
          this.timelineStayGateway.getStays({
            endDatetime: result.endDatetime,
            startDatetime: result.startDatetime,
            statuses: [result.status],
            userIds: [result.userId],
          }),
        );
        const stayToUpdate = stays.find((stay) => stay.id === result.id);

        this.store.dispatch([
          new TimelineUpsertNewStay(stayToUpdate || result),
          new TimelineUpdateShiftV2({
            ...previousShift,
            ignoreStayNecessary: ignoreStayNecessaryFormValue,
          }),
        ]);

        await this.timelineStaysSuggestionService.refreshStaysDataForSpecifiedUsers(
          [result.userId],
        );

        this.nzMessageService.success(
          this.translate.instant('general.saved_successfully'),
        );
        this.isSaveOperationOnGoingSignal.set(false);
        this.staysDrawerStateService.toggleCreateStaysDrawer(false);
      }
      return result;
    } catch (error) {
      console.error('An error occurred while creating a stay:', error);
      this.nzMessageService.error(
        this.translate.instant('general.something_went_wrong'),
      );
      this.isSaveOperationOnGoingSignal.set(false);
      throw error;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.staysDrawerStateService.resetSetShifts();
  }
}
